import './Object';
import { extend as extendObject } from './Object';
// Force implementation
extendObject();

declare global {
  interface Array<T> {
    chunk(n: Number | number): Array<T>;
    intersect(value: Array<T>): Array<T>;
    diff(value: Array<T>): Array<T>;
    unique(): Array<T>;
    uniqueBy(name: String | string): Array<T>;
    filled(trim: boolean): Array<T>;
    pluck(name: String | string): Array<any>;
    union(value: Array<T>): Array<T>;
    equal(value: Array<T>): boolean;
    includesAdv(searchElement: any, fromIndex?: Number | number | undefined): boolean;
    sortBy(field: String | string, desc: boolean, primer: ((item: any) => number)): Array<T>;
  }

  interface ArrayConstructor {
    diff(arrA: Array<any>, arrB: Array<any>): Array<any>; // Symetric diffrence !!!
    asArray<T>(arg: T | T[] | null): T[];
  }
};

extend();

export function extend() {
  Array.prototype.chunk = function (
    n: Number | number
  ): Array<any> {
    const chunkLength = Math.max((this.length / (n as number)), 1);
    let chunks = [];
    for (let i = 0; i < (n as number); i++)
      if ((chunkLength * (i + 1)) <= this.length)
        chunks.push(this.slice((chunkLength * i), (chunkLength * (i + 1))));
    return chunks;
  };

  // Intersection
  Array.prototype.intersect = function (
    value: Array<any>
  ): Array<any> {
    if (Array.isArray(value) && Array.isArray(this) && (value.length > 0) && (this.length > 0))
      return value.filter(value => this.includesAdv(value));
    return [];
  };

  // Diffrence
  Array.prototype.diff = function (
    value: Array<any>
  ): Array<any> {
    if (Array.isArray(value) && Array.isArray(this) && (value.length > 0) && (this.length > 0))
      return this.filter(item => (! value.includesAdv(item)));
    if (Array.isArray(this) && (this.length > 0))
      return this;
    return [];
  };

  // Unique check
  Array.prototype.unique = function (): Array<any> {
    let temp = [...new Set([...this])];
    if (temp.length > 0) {
      let result = [] as Array<any>;
      temp.forEach(function (elem: any) {
        if (Object.isObject(elem)) {
          if (result.filter(item => Object.equal(item, elem)).length < 1)
            result.push(elem);
        }
        else
          result.push(elem);
      });
      return result;
    }
    return [];
  };

  // Unique by specific parameter in object (check)
  Array.prototype.uniqueBy = function (
    name: String | string
  ): Array<Object> {
    let result = [] as Array<any>;
    this.forEach(function (elem: Object) {
      if (Object.isObject(elem)) {
        if ((name as string) in elem)
          if (result.filter((item: Object) => (((name as string) in item) ? ((item as any)[(name as string)] === (elem as any)[(name as string)]) : false))?.length < 1)
            result.push(elem);
      }
      else
        result.push(elem);
    });
    return result;
  };

  // Only filled values
  Array.prototype.filled = function (
    trim: boolean = true
  ): Array<any> {
    return this.filter((e: any) => {
      if (String.isString(e)) {
        if (trim)
          return (String(e).trim().length > 0);
        return (String(e).length > 0);
      }
      return ((e !== null) && (e !== undefined));
    });
  };

  // Pluck object in array into single array
  Array.prototype.pluck = function (
    name: String | string | Number | number | symbol
  ): Array<any> {
    return this.map((item: Object) => {
      if (Object.isObject(item)) {
        if ((name as (string | number | symbol)) in item)
          return ((item as any)[(name as (string | number | symbol))] as any);
        else
          return null;
      }
      throw 'Array does not contain object!';
    });
  };

  // Union/Uintersect
  Array.prototype.union = function (
    value: Array<any>
  ): Array<any> {
    if (Array.isArray(value) && Array.isArray(this) && (value.length > 0) && (this.length > 0))
      return [...this, ...value];
    if (Array.isArray(this) && (this.length > 0))
      return this;
    if (Array.isArray(value) && (value.length > 0))
      return value;
    return [];
  };

  // Equals
  Array.prototype.equal = function (
    value: Array<any>
  ): boolean {
    if (Array.isArray(value) && Array.isArray(this) && (value.length == this.length))
      return value.every(element => this.includes(element));
    return false;
  };

  // Includes with object comparsion
  Array.prototype.includesAdv = function (
    searchElement: any,
    fromIndex?: Number | number | undefined
  ): boolean {
    let result = false;
    if (! Object.isObject(searchElement))
      return this.includes(searchElement, (fromIndex as number));
    else
      for (let i = ((fromIndex as number) ?? 0); i < this.length; i++)
        if (Object.equal(this[i], searchElement)) {
          result = true;
          break;
        }
    return result;
  };

  // Sort by function
  Array.prototype.sortBy = function (
    field: String | string,
    desc: boolean = false,
    primer: ((item: any) => number) = parseFloat
  ): Array<any> {
    // Sort fn
    const sortFn = function (field: String | string, desc: boolean = false, primer: ((item: any) => number) = parseFloat): (a: any, b: any) => number {
      const key = (primer ? ((x: any) => primer(x[(field as string)])) : ((x: any) => x[(field as string)]));

      let descRes = ((! desc) ? 1 : -1);

      return function (a: any, b: any) {
        return (a = key(a)), (b = key(b)), (descRes * ((a > b) - (b > a)));
      };
    };

    return this.sort(sortFn(field, desc, primer)) as Array<any>;
  };

  // Symetric diffrence !!!
  Array.diff = function (
    arrA: Array<any>,
    arrB: Array<any>
  ): Array<any> {
    if (Array.isArray(arrA) && Array.isArray(arrB) && (arrA.length > 0) && (arrB.length > 0))
      return [...arrA.filter(item => (! arrB.includesAdv(item))), ...arrB.filter(item => (! arrA.includesAdv(item)))];
    if (Array.isArray(arrA) && (arrA.length > 0))
      return arrA;
    if (Array.isArray(arrB) && (arrB.length > 0))
      return arrB;
    return [];
  };

  Array.asArray = function asArray<T>(
    arg: T | T[] | null
  ): T[] {
    return (Array.isArray(arg) ? arg : ((arg !== null) ? [arg] : []));
  };
};

export {}; // Keeps it a valid module
